import styled from "@emotion/styled";
import type { FC } from "react";
import translate from "src/lib/translate";
import type { RefillRequirement } from "src/state/RefillRequirementsCubit/RefillRequirementsTypes";
import { isRefillCheckInActionable } from "src/state/RefillRequirementsCubit/RefillRequirementsHelpers";
import RefillRequirementRow from "./RefillRequirementRow";

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Card = styled.div`
  background: var(--color-white);
  border-radius: 8px;
  box-shadow: var(--light-shadow);
  overflow: hidden;
`;

// Matches the caption below the pharmacy tile (PharmacyInformationDetail: color-c-80): the
// charcoal-80 color and 16px left padding align it with the card content above.
const Hint = styled.p`
  margin: 0;
  padding-left: 1rem;
  font-size: 0.8125rem;
  color: var(--color-charcoal-80);
  line-height: 1.4;
`;

/**
 * Requirements checklist: a single card of divided rows + a home hint. Rows are display-only
 * except the outstanding refill check-in, which opens the refill survey via `onOpenSurvey`.
 */
const RefillRequirementsChecklist: FC<{
  requirements: RefillRequirement[];
  /** Renewal status — gates the check-in row so it only opens the survey at PENDING_SURVEY. */
  status?: string;
  onOpenSurvey?: () => void;
}> = ({ requirements, status, onOpenSurvey }) => {
  if (requirements.length === 0) {
    return null;
  }

  return (
    <Section>
      <Card>
        {requirements.map((requirement) => (
          <RefillRequirementRow
            key={requirement.type}
            requirement={requirement}
            onClick={
              onOpenSurvey && isRefillCheckInActionable(requirement, status)
                ? onOpenSurvey
                : undefined
            }
          />
        ))}
      </Card>
      <Hint>{translate("prepareRefillDialog.v2.requirements.homeHint")}</Hint>
    </Section>
  );
};

export default RefillRequirementsChecklist;
